'use client';
import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import type { User } from '../lib/api/types';



interface AuthContextType {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const USER_STORAGE_KEY = 'gs_auth_user';

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Initialize user from localStorage
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);
        
        if (storedUser) {
          try {
            setUserState(JSON.parse(storedUser));
          } catch (e) {
            console.error('Failed to parse stored user:', e);
            localStorage.removeItem(USER_STORAGE_KEY);
          }
        }
      }
    } catch (e) {
      console.error('Failed to read user data from storage:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Persist user to localStorage whenever it changes
  const setUser = useCallback((newUser: User | null) => {
    setUserState(newUser);
    
    if (typeof window !== 'undefined') {
      try {
        if (newUser) {
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(newUser));
        } else {
          localStorage.removeItem(USER_STORAGE_KEY);
        }
      } catch (e) {
        console.error('Failed to save user to storage:', e);
      }
    }
  }, []);

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Check if user has admin role
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      setUser,
      isAuthenticated,
      isAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
